import decodeHtml from '../../utilities/decodeHtml';
import SoundCloudApiTrack, {
  parseSoundCloudDate,
  SoundCloudAccess,
} from '../soundcloud/soundCloudApiTrack';
import Source from '../source';
import Track from './track';

/**
 * Release date as a timestamp. SoundCloud splits it into year/month/day and
 * often has none of them, in which case the upload date is the best there is.
 */
function releaseDateOf(track: SoundCloudApiTrack) {
  if (track.release_year) {
    return Date.UTC(track.release_year, (track.release_month || 1) - 1, track.release_day || 1);
  }

  return parseSoundCloudDate(track.created_at);
}

/**
 * A SoundCloud track as the library stores it.
 *
 * `id` is the URN, which is what every API call - including the one that
 * resolves a stream at play time - addresses the track by. The artist column
 * shows the uploader, because that is the credit the API terms ask for and
 * SoundCloud has no separate artist field.
 *
 * Two extra own properties beyond Track's. Both are plain data, so they
 * survive the JSON round trip through the library file exactly as `missing`
 * does; nothing here is an instance method (see ARCHITECTURE.md section 2).
 */
export default class SoundCloudTrack extends Track {
  static from(track: SoundCloudApiTrack) {
    return new SoundCloudTrack(track);
  }

  /**
   * The track's page on soundcloud.com. The API terms require a clearly
   * visible link back from every track shown - the "Open on SoundCloud" item
   * in the row menu.
   */
  permalinkUrl: string;

  /**
   * `playable`, `preview` or `blocked`, as of when the track was fetched.
   * Playback asks again; this is for the badge in lists.
   */
  access: SoundCloudAccess;

  constructor(track: SoundCloudApiTrack) {
    super(
      track.urn, // we throw on null right after
      Source.SoundCloud,
      decodeHtml(track.title || ''),
      track.user ? track.user.username : '',
      '',
      track.duration,
      track.genre || '',
      releaseDateOf(track),
    );

    if (!track.urn) throw new Error('Missing urn for SoundCloudTrack');

    // Always the same as artist here - SoundCloud has no artist field, so
    // both are the uploader. Set anyway, so the Uploader column means the
    // same thing in every list rather than being blank for one source.
    this.uploader = track.user ? track.user.username : '';

    this.permalinkUrl = track.permalink_url;
    this.access = track.access;
  }
}

/**
 * The stored `access` of a library track, if it is a SoundCloud one with a
 * sane value. Tracks come out of the library as plain JSON, so this reads
 * the property rather than testing the class.
 */
export function soundCloudAccess(track: Track | undefined): SoundCloudAccess | undefined {
  if (!track || track.type !== Source.SoundCloud) {
    return undefined;
  }

  const access = (track as SoundCloudTrack).access;

  return access === 'playable' || access === 'preview' || access === 'blocked' ? access : undefined;
}
